/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
/// <reference types="vite-plugin-svgr/client" />
import GenerateDraftIcon from "@/assets/generate_draft.svg?react";
import { Button } from "@/components/Elements";
import { useTrackEvent } from "@/features/analytics/api/trackUser";
import { useSubscription } from "@/features/auth/api/getSubscription";
import { GenerateContentBriefButton } from "@/features/auto-brief/components/GenerateContentBriefButton";
import { ImportTemplateButton } from "@/features/templates/components/ImportTemplateButton";
import { useEditorStore } from "@/stores/editor";
import { useNotificationStore } from "@/stores/notifications";
import { useSerpStore } from "@/stores/serp";
import { useDialogStore } from "@/stores/upgrade";
import { DocumentIcon } from "@heroicons/react/24/outline";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  KEY_SPACE_COMMAND,
} from "lexical";
import { useEffect, useRef, useState } from "react";
import { TbDownload, TbSparkles } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { FraseDocument } from "../../../../features/documents";
import { ImportContentUrlButton } from "../../../../features/editor/components/ImportContentUrlButton";

export const PlaceholderActionsPlugin = ({
  titleInputRef,
  setShowPlaceholders,
  showPlaceholders,
  setShowLinePlaceholder,
  setLastSelection,
  fraseDocument,
}: {
  titleInputRef: React.RefObject<HTMLTextAreaElement>;
  setShowPlaceholders: (show: boolean) => void;
  showPlaceholders: boolean;
  setShowLinePlaceholder: (show: boolean) => void;
  setLastSelection: (selection: any) => void;
  fraseDocument: FraseDocument;
}) => {
  const [importUrlDialogOpen, setImportUrlDialogOpen] = useState(false);
  const [importTemplateDialogOpen, setImportTemplateDialogOpen] =
    useState(false);
  const [generateContentBriefDialogOpen, setGenerateContentBriefDialogOpen] =
    useState(false);
  const [editor] = useLexicalComposerContext();
  const { data: subscriptionData, isLoading: isLoadingSubscription } =
    useSubscription({});
  const isAddOnActive = subscriptionData?.add_on;
  const optionsRef = useRef(null);
  const { editor: editorStore, setEditor } = useEditorStore();
  const { isPasting } = editorStore;
  const { serp: serpStore } = useSerpStore();
  const { mutate: trackEvent } = useTrackEvent();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const handleTrackActionClick = (action: string) => {
    trackEvent({
      event: "editor_action_click",
      properties: JSON.stringify({
        action,
      }),
    });
  };

  useEffect(() => {
    editor.update(() => {
      const root = $getRoot();
      const editorTextContent = root.getTextContent();
      const editorContent = root.getChildrenSize();
      if (editorTextContent.length === 0 && editorContent <= 1 && !isPasting) {
        setShowPlaceholders(true);
        setShowLinePlaceholder(false);
        titleInputRef.current?.focus();
      } else {
        editor.focus();
        setShowPlaceholders(false);
        setShowLinePlaceholder(true);
      }
    });

    const handleKeyDown = (event) => {
      // Check if the key pressed is 'Backspace'
      editor.update(() => {
        const root = $getRoot();
        if (!root) return;

        const editorTextContent = root.getTextContent();
        if (
          event.target.id === "lexical-content-editable" &&
          event.key === "Backspace" &&
          titleInputRef.current &&
          editorTextContent.length === 0
        ) {
          setShowPlaceholders(true);
          setShowLinePlaceholder(false);
          titleInputRef.current?.focus();
          setEditor((state) => {
            state.isPasting = false;
          });
        }
      });
    };

    const handleClickOutside = (event) => {
      if (event.target.id !== "lexical-editor-container") {
        return;
      }

      if (
        optionsRef.current &&
        !optionsRef.current.contains(event.target) &&
        titleInputRef.current &&
        !titleInputRef.current.contains(event.target) &&
        importUrlDialogOpen === false &&
        importTemplateDialogOpen === false
      ) {
        setShowLinePlaceholder(true);
        setShowPlaceholders(false); // Hide the options
        setTimeout(() => {
          editor.focus();
        }, 0);
      }
    };

    // Add event listeners
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup event listeners on component unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    editor,
    setShowPlaceholders,
    titleInputRef,
    importUrlDialogOpen,
    importTemplateDialogOpen,
  ]);

  if (showPlaceholders) {
    return (
      <div
        ref={optionsRef}
        className="flex flex-col items-start w-full px-5 transition-all"
      >
        <Button
          variant="text"
          startIcon={
            <DocumentIcon
              strokeWidth={1.5}
              style={{
                height: "1rem",
                width: "1rem",
              }}
              strokeColor="text-zinc-400"
              className="flex-shrink-0 stroke-zinc-600 dark:stroke-white text-zinc-400"
            />
          }
          size="md"
          className="w-full"
          textClassName="w-full text-left text-zinc-500 dark:text-zinc-500 hover:text-zinc-800 dark:hover:text-white"
          onClick={() => {
            setShowPlaceholders(false);
            setShowLinePlaceholder(true);
            setTimeout(() => {
              editor.focus();
            }, 0);
            handleTrackActionClick("empty_page");
          }}
        >
          Empty page
        </Button>
        <Button
          id="write-with-ai"
          variant="text"
          startIcon={
            <TbSparkles
              strokeWidth={1.5}
              style={{
                height: "1rem",
                width: "1rem",
                color: "#059669",
                fill: "#059669",
                strokeColor: "#059669",
              }}
              strokeColor="text-zinc-400"
              className="flex-shrink-0  text-emerald-600"
            />
          }
          size="md"
          className="w-full"
          textClassName="w-full text-left text-zinc-500 dark:text-zinc-500 hover:text-zinc-800 dark:hover:text-white"
          onClick={() => {
            setShowPlaceholders(false);
            setShowLinePlaceholder(true);
            editor.update(() => {
              const selection = $getSelection();
              if (selection && $isRangeSelection(selection)) {
                setLastSelection(selection);
              }

              setTimeout(() => {
                editor.focus();
                editor.dispatchCommand(KEY_SPACE_COMMAND, undefined);
              }, 0);
              handleTrackActionClick("write_with_ai");
            });
          }}
        >
          Start writing with AI...
        </Button>
        {isAddOnActive && (
          <Button
            variant="text"
            startIcon={
              <TbSparkles
                strokeWidth={1.5}
                style={{
                  height: "1rem",
                  width: "1rem",
                  color: "#059669",
                  fill: "#059669",
                  strokeColor: "#059669",
                }}
                strokeColor="text-zinc-400"
                className="flex-shrink-0  text-emerald-600"
              />
            }
            size="md"
            className="w-full"
            textClassName="w-full text-left text-zinc-500 dark:text-zinc-500 hover:text-zinc-800 dark:hover:text-white"
            onClick={() => {
              if (!isAddOnActive) {
                useDialogStore
                  .getState()
                  .openDialog("aiArticleUpgrade", "test");
              } else {
                navigate("/app/wizard", {
                  state: { shouldPrepopulate: true },
                });
              }
              handleTrackActionClick("generate_article_with_ai");
            }}
            disabled={isLoadingSubscription}
            isLoading={isLoadingSubscription}
          >
            {fraseDocument?.metadata?.ai_article_started
              ? "Continue generating article with AI..."
              : "Generate article with AI..."}
          </Button>
        )}
        {/*serpStore[fraseDocument.id]?.serpLoaded && (*/}
        <GenerateContentBriefButton
          open={generateContentBriefDialogOpen}
          setOpen={setGenerateContentBriefDialogOpen}
          setShowLinePlaceholder={setShowLinePlaceholder}
          trigger={
            <Button
              variant="text"
              startIcon={
                <GenerateDraftIcon
                  strokeWidth={1.5}
                  style={{
                    height: "1rem",
                    width: "1rem",
                    color: "#059669",
                    strokeColor: "#059669",
                  }}
                  strokeColor="text-zinc-400"
                  className="flex-shrink-0  text-emerald-600"
                />
              }
              onClick={(event) => {
                if (!serpStore[fraseDocument.id]?.serpLoaded) {
                  addNotification({
                    type: "info",
                    title: "Search results not processed",
                    message:
                      "Please enter a search query and process the search results before generating a content brief.",
                  });
                  event.preventDefault();
                  return;
                }
              }}
              size="md"
              className="w-full"
              textClassName="w-full text-left text-zinc-500 dark:text-zinc-500 hover:text-zinc-800 dark:hover:text-white"
            >
              Generate content brief
            </Button>
          }
          setShowPlaceholders={setShowPlaceholders}
        />

        {!fraseDocument.metadata?.url && (
          <ImportContentUrlButton
            setCustomUrls={() => {}}
            open={importUrlDialogOpen}
            setOpen={setImportUrlDialogOpen}
            setShowPlaceholders={setShowPlaceholders}
            trigger={
              <Button
                variant="text"
                startIcon={
                  <TbDownload
                    strokeWidth={1.5}
                    style={{
                      height: "1rem",
                      width: "1rem",
                    }}
                    className="flex-shrink-0 stroke-zinc-600 dark:stroke-white"
                  />
                }
                onClick={() => {
                  handleTrackActionClick("import_content_from_url");
                }}
                size="md"
                className="w-full"
                textClassName="w-full text-left text-zinc-500 dark:text-zinc-500 hover:text-zinc-800 dark:hover:text-white"
              >
                Import content from URL
              </Button>
            }
          />
        )}

        <ImportTemplateButton
          setCustomUrls={() => {}}
          open={importTemplateDialogOpen}
          setOpen={setImportTemplateDialogOpen}
          setShowPlaceholders={setShowPlaceholders}
          trigger={
            <Button
              variant="text"
              startIcon={
                <TbDownload
                  strokeWidth={1.5}
                  style={{
                    height: "1rem",
                    width: "1rem",
                  }}
                  className="flex-shrink-0 stroke-zinc-600 dark:stroke-white"
                />
              }
              onClick={() => {
                handleTrackActionClick("import_template");
              }}
              size="md"
              className="w-full"
              textClassName="w-full text-left text-zinc-500 dark:text-zinc-500 hover:text-zinc-800 dark:hover:text-white"
            >
              Import template
            </Button>
          }
          shouldSubmit={false}
          onSubmission={function (): void {
            throw new Error("Function not implemented.");
          }}
        />

        {/*
        <Button
          variant="text"
          startIcon={
            <TbFileDownload
              strokeWidth={1.5}
              style={{
                height: "1rem",
                width: "1rem",
              }}
              className="flex-shrink-0 stroke-zinc-600 dark:stroke-white"
            />
          }
          size="md"
          className="w-full"
          textClassName="w-full text-left text-zinc-500 dark:text-zinc-500 hover:text-zinc-800 dark:hover:text-white"
          onClick={handleRenderPlaceholderFeature}
        >
          Import template
        </Button>*/}
      </div>
    );
  } else {
    return null;
  }
};

export default PlaceholderActionsPlugin;
